package co.maytes.standupbot.dashboard.pages.app.standupruns

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.feedback.EmptyState
import co.maytes.standupbot.dashboard.components.feedback.ErrorState
import co.maytes.standupbot.dashboard.components.feedback.SkeletonList
import co.maytes.standupbot.dashboard.components.layout.AppShell
import co.maytes.standupbot.dashboard.components.motion.Revalidating
import co.maytes.standupbot.dashboard.components.motion.Staggered
import co.maytes.standupbot.dashboard.components.motion.StateSwap
import co.maytes.standupbot.dashboard.components.layout.AuthGuard
import co.maytes.standupbot.dashboard.components.widgets.AppPageHeader
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.SegmentedControl
import co.maytes.standupbot.dashboard.components.widgets.SegmentedOption
import co.maytes.standupbot.dashboard.components.widgets.icons.LucidePlay
import co.maytes.standupbot.dashboard.features.runslist.RunsListContainer
import co.maytes.standupbot.dashboard.features.runslist.RunsListContract
import co.maytes.standupbot.dashboard.features.standupruns.RunListDensity
import co.maytes.standupbot.dashboard.features.standupruns.StandupFilterBar
import co.maytes.standupbot.dashboard.features.standupruns.StandupFilterState
import co.maytes.standupbot.dashboard.features.standupruns.StandupListItem
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.stores.rememberApiClient
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import com.kttipay.mvi.compose.subscribe
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.localStorage
import org.jetbrains.compose.web.css.px

@Page("/app/runs")
@Composable
fun StandupsIndexPage() = AuthGuard {
    AppShell {
        RunsListScreen()
    }
}

@Composable
private fun RunsListScreen() {
    val auth = rememberAuthStore()
    val api = rememberApiClient()
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    val container = remember(api) { RunsListContainer(scope, api) }

    val state by container.subscribe { action ->
        when (action) {
            is RunsListContract.Action.Navigate -> ctx.router.navigateTo(action.path)
        }
    }

    val workspaceId = auth.currentUser?.workspace?.id
    LaunchedEffect(workspaceId) {
        if (!workspaceId.isNullOrBlank()) {
            container.intent(RunsListContract.Intent.Load(workspaceId))
        }
    }

    var density by remember { mutableStateOf(readStoredDensity()) }

    RunsListView(
        state = state,
        density = density,
        onDensityChange = {
            density = it
            writeStoredDensity(it)
        },
        dispatch = { container.intent(it) },
    )
}

private const val DENSITY_STORAGE_KEY = "sb-runs-density"

private fun readStoredDensity(): RunListDensity = try {
    if (localStorage.getItem(DENSITY_STORAGE_KEY) == "dense") RunListDensity.Dense else RunListDensity.Comfortable
} catch (_: Throwable) {
    RunListDensity.Comfortable
}

private fun writeStoredDensity(density: RunListDensity) {
    try {
        localStorage.setItem(DENSITY_STORAGE_KEY, if (density == RunListDensity.Dense) "dense" else "comfortable")
    } catch (_: Throwable) {
    }
}

@Composable
private fun RunsListView(
    state: RunsListContract.State,
    density: RunListDensity,
    onDensityChange: (RunListDensity) -> Unit,
    dispatch: (RunsListContract.Intent) -> Unit,
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier { property("margin", "0 auto") }
            .maxWidth(1200.px),
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            AppPageHeader(
                eyebrow = "history",
                title = "Runs",
                subtitle = "Every standup this workspace has posted — filter by standup, status, or date.",
            )

            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween,
            ) {
                Box(modifier = Modifier.styleModifier { property("flex", "1 1 auto"); property("min-width", "0") }) {
                    StandupFilterBar(
                        standups = state.standups,
                        current = state.filters,
                        onChange = { dispatch(RunsListContract.Intent.SetFilters(it)) },
                        onClear = { dispatch(RunsListContract.Intent.ClearFilters) },
                    )
                }
                DensityToggle(active = density, onSelect = onDensityChange)
            }

            Box(modifier = Modifier.fillMaxWidth().margin(top = Spacing.lg)) {
                RunsListBody(state, density, dispatch)
            }
        }
    }
}

private val densityOptions = listOf(
    SegmentedOption(RunListDensity.Comfortable, "Comfortable"),
    SegmentedOption(RunListDensity.Dense, "Dense"),
)

@Composable
private fun DensityToggle(
    active: RunListDensity,
    onSelect: (RunListDensity) -> Unit,
) {
    SegmentedControl(
        options = densityOptions,
        selected = active,
        onSelect = onSelect,
        ariaLabel = "List density",
    )
}

@Composable
private fun RunsListBody(
    state: RunsListContract.State,
    density: RunListDensity,
    dispatch: (RunsListContract.Intent) -> Unit,
) {
    StateSwap(state = state.phase) {
        when (val status = state.status) {
            RunsListContract.Status.Idle, RunsListContract.Status.Loading -> SkeletonList()
            is RunsListContract.Status.Error -> ErrorState(
                message = status.error.displayMessage,
                onRetry = {
                    state.workspaceId?.let { dispatch(RunsListContract.Intent.Load(it)) }
                },
            )
            RunsListContract.Status.Loaded,
            RunsListContract.Status.LoadingMore,
            RunsListContract.Status.Refreshing,
            -> Revalidating(refreshing = status == RunsListContract.Status.Refreshing) {
                if (state.isEmpty) {
                    EmptyState(
                        title = "No runs yet",
                        subtitle = "Once a standup's schedule fires, every posted run lands here — ready to review and share.",
                        icon = { LucidePlay(size = 26) },
                        ctaLabel = "Set up your first standup",
                        onCta = { dispatch(RunsListContract.Intent.GoToOnboarding) },
                    )
                } else {
                    RunsListItems(state, density, dispatch)
                }
            }
        }
    }
}

@Composable
private fun RunsListItems(
    state: RunsListContract.State,
    density: RunListDensity,
    dispatch: (RunsListContract.Intent) -> Unit,
) {
    Column(modifier = Modifier.fillMaxWidth()) {
        val sorted = remember(state.items) {
            state.items.sortedByDescending { it.postedAt ?: it.scheduledFor }
        }
        val standupsById = remember(state.standups) {
            state.standups.associateBy { it.id }
        }
        Staggered(items = sorted, key = { it.id }, animate = !state.hasEntered) { item ->
            StandupListItem(
                item = item,
                standup = item.standupId?.let { standupsById[it] },
                density = density,
            ) { id ->
                dispatch(RunsListContract.Intent.OpenRun(id))
            }
        }
        if (state.pageInfo?.hasMore == true) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .margin(top = Spacing.md),
                contentAlignment = Alignment.Center,
            ) {
                Button(
                    label = if (state.status == RunsListContract.Status.LoadingMore) "Loading…" else "Load more",
                    variant = ButtonVariant.Ghost,
                    enabled = state.status != RunsListContract.Status.LoadingMore,
                    onClick = { dispatch(RunsListContract.Intent.LoadMore) },
                )
            }
        }
    }
}
